import { t } from 'Src/i18n';
import { isSeller } from 'Utils/seller/sellerUtils';
import { API_DATE_FORMAT, formatApiDate, momentFromApiDate } from 'Utils/date/apiDate';
import { upperCaseLetterToInt } from 'Components/utils/string/string.util';
import type { ServicesContextType } from 'Context/ServicesContext';
import 'Context/ServicesContext';
import { capitalizeWordFirstCaracter } from 'Utils/string/stringUtils';
export const MORNING = 'MORNING';
export const AFTERNOON_BEFORE_5PM = 'AFTERNOON_BEFORE_5PM';
export const AFTERNOON_AFTER_5PM = 'AFTERNOON_AFTER_5PM';
export const AFTERNOON_BEFORE_4PM = 'AFTERNOON_BEFORE_4PM';
export const AFTERNOON_AFTER_4PM = 'AFTERNOON_AFTER_4PM';
export const EVENING = 'EVENING';
// childcares
export const AFTERNOON = 'AFTERNOON';
export const RENTAL = 'RENTAL';
export const getSlotKey = ({
  slotName,
  startDate,
  endDate,
  attendeeId,
}: {
  slotName: string;
  startDate: string;
  endDate: string;
  attendeeId: string;
}) => `${slotName}-${startDate}-${endDate}-${attendeeId}`;

type SlotAvailability = {
  afternoonBefore4Slot?: unknown;
  afternoonBefore5Slot?: unknown;
  afternoonGenericSlot?: unknown;
};

type EveningSlotAvailability = {
  afternoonAfter4Slot?: unknown;
  afternoonAfter5Slot?: unknown;
  eveningGenericSlot?: unknown;
};

export const resolveAfternoonSlotType = ({
  afternoonBefore4Slot,
  afternoonBefore5Slot,
  afternoonGenericSlot,
}: SlotAvailability) => {
  if (afternoonBefore4Slot) {
    return AFTERNOON_BEFORE_4PM;
  }

  if (afternoonBefore5Slot) {
    return AFTERNOON_BEFORE_5PM;
  }

  if (afternoonGenericSlot) {
    return AFTERNOON;
  }

  return AFTERNOON_BEFORE_5PM;
};

export const resolveEveningSlotType = ({
  afternoonAfter4Slot,
  afternoonAfter5Slot,
  eveningGenericSlot,
}: EveningSlotAvailability) => {
  if (afternoonAfter4Slot) {
    return AFTERNOON_AFTER_4PM;
  }

  if (afternoonAfter5Slot) {
    return AFTERNOON_AFTER_5PM;
  }

  if (eveningGenericSlot) {
    return EVENING;
  }

  return AFTERNOON_AFTER_5PM;
};

export const getAfternoonSlotSubtitle = (afternoonSlotType: string) =>
  afternoonSlotType === AFTERNOON_BEFORE_4PM ? 'before4PM' : 'before5PM';

export const getEveningSlotSubtitle = (eveningSlotType: string) =>
  eveningSlotType === AFTERNOON_AFTER_4PM ? 'after4PM' : 'after5PM';

export const getAttendeeLabel = (
  attendeesList: ServicesContextType['attendeesList'],
  attendeeId: string,
  appContext: AppContext,
) => {
  const { unicodeFirstName, firstName, unicodeLastName, lastName, birthdate, isChild } =
    attendeesList.find(({ id }) => id === attendeeId) || {};
  const attendeeFirstName =
    unicodeFirstName?.trim() || capitalizeWordFirstCaracter(firstName?.trim() || '');
  const attendeeLastName =
    unicodeLastName?.trim() || capitalizeWordFirstCaracter(lastName?.trim() || '');
  let attendeelabel = `${String(attendeeFirstName)}${attendeeFirstName ? ' ' : ''}${String(attendeeLastName)}`;

  if (attendeelabel && isSeller(appContext) && isChild && birthdate) {
    attendeelabel += ` (${formatApiDate(birthdate, t('common.dateFormatTechnicalID'))})`;
  }

  return isChild && birthdate && !attendeelabel
    ? t('booking.services.childcare.forWhatKid', {
        birthdate: formatApiDate(birthdate, t('common.dateFormatTechnicalID')),
      })
    : attendeelabel ||
        `${t(!isChild ? 'booking.common.adult' : 'booking.common.child')} ${upperCaseLetterToInt(attendeeId)}`;
};
type DateRangeSchedule = {
  startDate: string;
  endDate: string;
};

export const isSingleDaySchedule = ({ startDate, endDate }: DateRangeSchedule) =>
  startDate === endDate;

export const isMultiDaySchedule = ({ startDate, endDate }: DateRangeSchedule) =>
  startDate !== endDate;

export const getHolidayInnerRangeDates = (arrivalDate: string, departureDate: string) => {
  const innerStart = momentFromApiDate(arrivalDate).add(1, 'day');
  const innerEnd = momentFromApiDate(departureDate).subtract(1, 'day');
  const isValid = !innerStart.isAfter(innerEnd);

  return {
    startDate: isValid ? innerStart.format(API_DATE_FORMAT) : null,
    endDate: isValid ? innerEnd.format(API_DATE_FORMAT) : null,
    isValid,
  };
};

export const isHolidayFullStaySchedule = (
  schedule: DateRangeSchedule,
  arrivalDate: string,
  departureDate: string,
) =>
  isMultiDaySchedule(schedule) &&
  schedule.startDate === arrivalDate &&
  schedule.endDate === departureDate;

export const isHolidayInnerRangeSchedule = (
  schedule: DateRangeSchedule,
  arrivalDate: string,
  departureDate: string,
) => {
  const { startDate, endDate, isValid } = getHolidayInnerRangeDates(arrivalDate, departureDate);

  return (
    isValid &&
    isMultiDaySchedule(schedule) &&
    schedule.startDate === startDate &&
    schedule.endDate === endDate
  );
};

export const isHolidayBundledSchedule = (
  schedule: DateRangeSchedule,
  arrivalDate: string,
  departureDate: string,
) =>
  isHolidayFullStaySchedule(schedule, arrivalDate, departureDate) ||
  isHolidayInnerRangeSchedule(schedule, arrivalDate, departureDate);

export const isHolidayBoundaryDay = (
  schedule: DateRangeSchedule,
  arrivalDate: string,
  departureDate: string,
) =>
  isSingleDaySchedule(schedule) &&
  (schedule.startDate === arrivalDate || schedule.startDate === departureDate);

export const isDayInHolidayInnerRange = (
  schedule: DateRangeSchedule,
  arrivalDate: string,
  departureDate: string,
) => {
  const { startDate, endDate, isValid } = getHolidayInnerRangeDates(arrivalDate, departureDate);

  if (!isValid || !isSingleDaySchedule(schedule)) {
    return false;
  }

  return schedule.startDate >= startDate && schedule.startDate <= endDate;
};

export const activityOrEquipementDetailClick = (
  isCancellingActivity: boolean,
  isRental: boolean,
) => {
  if (isRental) {
    return isCancellingActivity ? 'remove_ski_equipment' : 'add_ski_equipment';
  }

  return isCancellingActivity ? 'remove_activity' : 'add_activity';
};

type SelectedScheduleFormValue = {
  isSelected: boolean;
  startDate: string;
  endDate: string;
  timeSlots: string[];
};

type AttendeeScheduleFormValues = {
  attendees: Record<
    string,
    {
      selected: boolean;
      schedules: SelectedScheduleFormValue[];
    }
  >;
  slots: Record<string, { serviceId: string }>;
};

export const buildAttendeesReservationsFromSelectedSchedules = (
  values: AttendeeScheduleFormValues,
  productInfoId: string,
) => {
  const attendeeIds: string[] = [];
  const attendeesReservations: Array<{
    customerId: string;
    productInfoId: string;
    schedules: Array<{ startDate: string; endDate: string; slots: string[] }>;
  }> = [];
  // Childcare update uses a delete-then-replace flow: all slot ids must be sent so
  // previously rented bundles or single days can be removed before re-adding the selection.
  const serviceIds = [
    ...new Set(
      Object.values(values.slots)
        .map((slot) => slot.serviceId)
        .filter((serviceId): serviceId is string => Boolean(serviceId)),
    ),
  ];

  Object.entries(values.attendees).forEach(([customerId, attendee]) => {
    if (!attendee.selected) {
      return;
    }

    attendeeIds.push(customerId);

    const selectedSchedules = attendee.schedules.filter(({ isSelected }) => isSelected);

    if (!selectedSchedules.length) {
      return;
    }

    const schedules = selectedSchedules.map(({ startDate, endDate, timeSlots }) => {
      const slots = timeSlots
        .map((slotName) => {
          const slotKey = getSlotKey({ slotName, startDate, endDate, attendeeId: customerId });
          return values.slots[slotKey]?.serviceId;
        })
        .filter((serviceId): serviceId is string => Boolean(serviceId));

      return { startDate, endDate, slots };
    });

    attendeesReservations.push({
      customerId,
      productInfoId,
      schedules,
    });
  });

  return { attendeesReservations, serviceIds, attendeeIds };
};
